<template>
  <div class="org-admin-layout-container" :class="teamLayoutClasses" @keydown.esc="closeTeamNavigation" @keydown.tab="trapTeamNavigationFocus">
    <el-container class="org-admin-container">
      <el-header class="header-bar">
        <div class="header-inner">
          <div class="title-wrap">
            <button v-if="displayState.isIPad && displayState.layoutMode === 'mobile'" ref="teamMenuToggle" class="org-team-menu-toggle" type="button" :aria-label="$t('layout.layout.team_management')" :aria-expanded="teamNavigationExpanded" aria-controls="org-team-navigation" @click="toggleTeamNavigation">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            <div class="header-team-identity">
              <span class="header-team-avatar" aria-hidden="true">
                <img
                  v-if="currTeamInfo.avatar"
                  :src="currTeamInfo.avatar"
                  alt=""
                >
                <i
                  v-else
                  class="icon ag-iconfont"
                  :class="Number(currTeamInfo.team_type) > 0 ? 'icon-user' : 'icon-company'"
                />
              </span>
              <strong :title="currTeamInfo.name">
                {{ currTeamInfo.name || $t('layout.layout.current_team') }}
              </strong>
            </div>
            <div class="line" />
            <div class="back-home">
              <a href="/" :aria-label="$t('layout.layout.exit_management')">
                <i
                  class="icon ag-iconfont icon-exit-line"
                />
                <div class="back-text">
                  {{ $t("layout.layout.exit_management") }}
                </div>
              </a>
            </div>
          </div>
          <div class="options">
            <div
              ref="userCard"
              class="user-profile"
              @mouseenter="openUserCardOnHover"
              @mouseleave="scheduleUserCardClose"
              @focusin="openUserCardOnHover"
              @keydown.down.prevent="openUserCard"
              @focusout="handleUserCardFocusOut"
              @keydown.esc="closeUserCard"
            >
              <button
                class="user-profile-trigger"
                type="button"
                aria-haspopup="dialog"
                :aria-expanded="userCardVisible"
                @click.stop="toggleUserCard"
              >
                <UserAvatarBadge
                  :size="36"
                  :src="userAvatar"
                  :subscription-key="$store.getters['user/user_sub']"
                />
              </button>

              <transition name="user-card-fade">
                <section
                  v-show="userCardVisible"
                  class="user-profile-card"
                  role="dialog"
                  :aria-label="$store.getters['user/nick_name']"
                  @click.stop
                >
                  <div class="user-profile-card-head">
                    <UserAvatarBadge
                      :size="48"
                      :src="userAvatar"
                      :subscription-key="$store.getters['user/user_sub']"
                    />
                    <div class="user-profile-copy">
                      <strong :title="$store.getters['user/nick_name']">
                        {{ $store.getters['user/nick_name'] || '-' }}
                      </strong>
                      <span :title="userContact">{{ userContact }}</span>
                    </div>
                  </div>

                  <div class="user-points-row">
                    <span class="user-points-icon" aria-hidden="true">
                      <i class="icon ag-iconfont icon-gift-box" />
                    </span>
                    <span>{{ $t('layout.layout.available_points') }}</span>
                    <strong>{{ displayPoints }}</strong>
                  </div>
                </section>
              </transition>
            </div>
          </div>
        </div>
      </el-header>
      <el-container class="org-admin-body-container">
        <button v-if="teamNavigationOverlay" class="org-team-navigation-backdrop" type="button" :aria-label="$t('common.close')" @click="closeTeamNavigation" />
        <el-aside
          id="org-team-navigation"
          class="org-admin-layout-aside"
          width="252px"
          :role="teamNavigationOverlay ? 'dialog' : undefined"
          :aria-modal="teamNavigationOverlay ? 'true' : undefined"
          :aria-label="$t('layout.layout.team_management')"
          :inert="displayState.isIPad && displayState.layoutMode === 'mobile' && displayState.layoutTier === 'compact' && !teamNavigationOpen ? '' : null"
        >
          <div ref="teamNavigation" class="aside-inner" tabindex="-1">
            <el-select
              v-if="isMobileView && !displayState.isIPad"
              ref="mobileNavigation"
              class="org-admin-mobile-menu"
              :model-value="menuActiveIndex"
              :aria-label="$t('layout.layout.team_management')"
              :placeholder="$t('layout.layout.team_management')"
              popper-class="org-admin-mobile-menu-popper"
              fit-input-width
              @change="changeMobileNav"
            >
              <el-option
                v-for="item in mobileMenuItems"
                :key="item.code"
                :value="item.code"
                :label="item.title"
              />
            </el-select>
            <nav v-else class="company-menu-box" :aria-label="$t('layout.layout.team_management')">
            <div
              v-for="(menuItem,menuItemIndex) in visibleMenus"
              :key="menuItemIndex"
              class="company-menu-top-item"
            >
              <button
                v-for="(subMenuItem,subMenuItemIndex) in menuItem.children"
                :key="subMenuItemIndex"
                class="company-menu-sub-item"
                type="button"
                :aria-current="menuActiveIndex === subMenuItem.code ? 'page' : undefined"
                :aria-label="subMenuItem.title"
                :title="subMenuItem.title"
                @click="changeNav(subMenuItem)"
              >
                <el-icon v-if="displayState.isIPad && displayState.layoutMode === 'mobile'" class="org-team-navigation-icon" aria-hidden="true"><component :is="teamNavigationIcon(subMenuItem.code)" /></el-icon>
                <div
                  class="top-item-text"
                  :class="menuActiveIndex==subMenuItem.code?'active':''"
                >
                  <div
                    class="top-item-title"
                  >
                    {{ subMenuItem.title }}
                  </div>
                </div>
              </button>
            </div>
            </nav>
          </div>
          <!--          <el-menu
            :default-active="menuActiveIndex"
            class="menu-box"
            :collapse="menuCollapse"
            @open="openMenu"
            @close="closeMenu"
          >
            <div
              v-for="(menuItem,menuItemIndex) in menus"
              :key="menuItemIndex"
              class="menu-items-box"
            >
              <el-menu-item
                v-if="(!menuItem.children||menuItem.children.length===0)&&menuItem.show"
                :index="menuItem.code"
                @click="changeNav(menuItem)"
              >
                <span
                  v-if="menuItem.icon"
                  class="menu-icon"
                >
                  <i
                    class="menu-item-icon icon ag-iconfont"
                    :class="'icon-'+menuItem.icon"
                  />
                </span>
                <template #title>
                  <div class="menu-title">{{ menuItem.title }}</div>
                </template>
              </el-menu-item>
              <el-sub-menu
                v-if="menuItem.children&&menuItem.children.length>0&&menuItem.show"
                :index="menuItem.code"
              >
                <template #title>
                  <span
                    v-if="menuItem.icon"
                    class="menu-icon"
                  >
                    <i
                      class="menu-item-icon icon ag-iconfont"
                      :class="'icon-'+menuItem.icon"
                    />
                  </span>
                  <div
                    class="menu-title"
                    :class="menuCollapse?'hide-sub-title':''"
                  >{{ menuItem.title }}
                  </div>
                </template>
                <div
                  v-for="(subMenuItem,subMenuItemIndex) in menuItem.children"
                  :key="subMenuItemIndex"
                  class="sub-menu-box"
                >
                  <el-menu-item
                    v-if="(!subMenuItem.children||subMenuItem.children.length===0)&&subMenuItem.show"
                    :index="subMenuItem.code"
                    @click="changeNav(subMenuItem)"
                  >
                    <span
                      v-if="subMenuItem.icon"
                      class="menu-icon"
                    >
                      <i
                        class="menu-item-icon icon ag-iconfont"
                        :class="'icon-'+subMenuItem.icon"
                      />
                    </span>
                    <template #title>
                      <div class="menu-title">{{ subMenuItem.title }}</div>
                    </template>
                  </el-menu-item>
                  <el-sub-menu
                    v-if="subMenuItem.children&&subMenuItem.children.length>0&&subMenuItem.show"
                    :index="subMenuItem.code"
                  >
                    <template #title>
                      <span
                        v-if="subMenuItem.icon"
                        class="menu-icon"
                      >
                        <i
                          class="menu-item-icon icon ag-iconfont"
                          :class="'icon-'+subMenuItem.icon"
                        />
                      </span>
                      <div class="menu-title">{{ subMenuItem.title }}</div>
                    </template>
                    <el-menu-item
                      v-for="(sSubMenuItem,sSubMenuItemIndex) in subMenuItem.children"
                      :key="sSubMenuItemIndex"
                      :index="sSubMenuItem.code"
                      @click="changeNav(sSubMenuItem)"
                    >
                      <span
                        v-if="sSubMenuItem.icon"
                        class="menu-icon"
                      >
                        <i
                          class="menu-item-icon icon ag-iconfont"
                          :class="'icon-'+sSubMenuItem.icon"
                        />
                      </span>
                      <template #title>
                        <div class="menu-title">{{ sSubMenuItem.title }}</div>
                      </template>
                    </el-menu-item>
                  </el-sub-menu>
                </div>
              </el-sub-menu>
            </div>
          </el-menu>-->
        </el-aside>
        <el-main class="org-admin-layout-main" :inert="teamNavigationOverlay ? '' : null">
          <div class="org-admin-main-inner">
            <router-view v-slot="{ Component }">
              <transition
                mode="out-in"
                name="move"
              >
                <component
                  v-if="accessChecked"
                  :is="Component"
                  :key="`${$route.name}:${currentTeamId}`"
                  @changeNav="changeNav"
                />
              </transition>
            </router-view>
          </div>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>

import UserAvatarBadge from '@/components/UserAvatarBadge/index.vue'
import { OfficeBuilding, User, Checked, Key, Wallet, DataLine } from '@element-plus/icons-vue'
import { useDisplayState } from '@/views/mobile/shared/useDisplayState'

export default {
  name: 'OrgAdminLayout',
  components: {
    UserAvatarBadge, OfficeBuilding, User, Checked, Key, Wallet, DataLine
  },
  setup() { return useDisplayState() },
  data() {
    return {
      menus: [
        {
          code: '1', name: 'OrgAdmin', show: true, icon: 'company', title: this.$t('layout.layout.team_management'), path: '/org-admin/index',
          children: [
            {
              code: '1_1',
              name: 'OrgAdminBase',
              show: true,
              icon: '',
              title: this.$t('layout.layout.organization_info'),
              path: '/org-admin/index',
              teamRole: 'team_manage',
              children: []
            },
            {
              code: '1_2',
              name: 'MemberDepartment',
              show: true,
              icon: '',
              title: this.$t('layout.layout.member_management'),
              path: '/org-admin/member-department',
              teamRoles: ['team_manage', 'team_manage.member'],
              children: []
            },
            {
              code: '1_3',
              name: 'MemberApprove',
              show: true,
              icon: '',
              title: this.$t('layout.layout.member_approval'),
              path: '/org-admin/member-approve',
              teamRoles: ['team_manage', 'team_manage.invitation'],
              children: []
            },
            {
              code: '1_4',
              name: 'TeamRoleManagement',
              show: true,
              icon: '',
              title: this.$t('layout.layout.role_management'),
              path: '/org-admin/roles',
              teamRoles: ['team_manage', 'team_manage.role'],
              ownerOnly: false,
              children: []
            },
            {
              code: '1_5',
              name: 'TeamLimitManagement',
              show: true,
              icon: '',
              title: this.$t('layout.layout.limit_management'),
              path: '/org-admin/limits',
              teamRoles: ['team_manage', 'team_manage.points_limit'],
              ownerOnly: false,
              children: []
            },
            {
              code: '1_6',
              name: 'TeamConsumptionRecords',
              show: true,
              icon: '',
              title: this.$t('layout.layout.consumption_records'),
              path: '/org-admin/consumption-records',
              ownerOnly: false,
              children: []
            }
          ]
        }
      ],
      curPath: '/org-admin/index',
      menuActiveIndex: '1_1',
      menuCollapse: false,
      teamNavigationOpen: false,
      teamWideCollapsed: false,
      accessChecked: false,
      userCardVisible: false,
      userCardCloseTimer: null,
      viewportQuery: null,
      isMobileView: typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
      layoutActive: false
    }
  },
  computed: {
    teamNavigationExpanded() {
      return this.displayState.layoutTier === 'wide' ? !this.teamWideCollapsed : this.teamNavigationOpen
    },
    teamNavigationOverlay() {
      return this.displayState.isIPad && this.displayState.layoutMode === 'mobile' && this.displayState.layoutTier !== 'wide' && this.teamNavigationOpen
    },
    teamLayoutClasses() {
      return {
        'org-admin-ipad': this.displayState.isIPad && this.displayState.layoutMode === 'mobile',
        [`org-admin-${this.displayState.layoutTier}`]: this.displayState.isIPad && this.displayState.layoutMode === 'mobile',
        'org-team-navigation-open': this.teamNavigationOpen,
        'org-team-navigation-collapsed': this.teamWideCollapsed
      }
    },
    currentTeamId() {
      return Number(this.$store.getters['user/current_team_id']) || 0
    },
    currTeamInfo() {
      return this.$store.getters['team/currentTeam'] || {}
    },
    currentTeamDetail() {
      return this.$store.getters['team/currentTeamInfo'] || {}
    },
    memberPointsLimit() {
      return this.$store.getters['team/memberPointsLimit'] || {}
    },
    currentTeamRoles() {
      return Array.isArray(this.currTeamInfo.team_roles) ? this.currTeamInfo.team_roles : []
    },
    visibleMenus() {
      return this.menus.map(menu => ({
        ...menu,
        children: menu.children.filter(item => this.canShowMenu(item))
      })).filter(menu => menu.children.length > 0)
    },
    mobileMenuItems() {
      return this.visibleMenus.flatMap(menu => menu.children)
    },
    teamPointsSurplus() {
      const numericalAuth = this.currentTeamDetail.numerical_auth || this.currTeamInfo.numerical_auth || {}
      const pointsQuota = numericalAuth.points_quota || numericalAuth
      return Number(pointsQuota.points_quota_surplus_numerical) || 0
    },
    displayPoints() {
      if (Number(this.memberPointsLimit.points_limit_enabled) === 1) {
        return Number(this.memberPointsLimit.points_remaining_numerical) || 0
      }
      return this.teamPointsSurplus
    },
    userAvatar() {
      return this.$store.getters['user/avatar'] || require('@/assets/img/def_user.png')
    },
    userContact() {
      return this.$store.getters['user/email'] ||
        this.$store.getters['user/phone'] ||
        this.$store.getters['user/user_name'] ||
        '-'
    }
  },
  watch: {
    'displayState.layoutMode'() {
      this.teamNavigationOpen = false
      this.closeUserCard()
    },
    'displayState.layoutTier'() {
      // A size change only dismisses the overlay; the route and page stay mounted.
      this.teamNavigationOpen = false
    },
    '$route.name'(name) {
      this.closeTeamNavigation()
      this.accessChecked = false
      this.initMenuActiveIndex(name)
      this.checkCurrentAccess()
    },
    currentTeamId() {
      this.accessChecked = false
      this.closeUserCard()
      this.$nextTick(() => this.checkCurrentAccess())
    },
    currentTeamRoles: {
      deep: true,
      handler() {
        if (this.layoutActive) this.checkCurrentAccess()
      }
    }
  },
  mounted() {
    this.layoutActive = true
    this.viewportQuery = window.matchMedia('(max-width: 768px)')
    this.isMobileView = this.viewportQuery.matches
    this.viewportQuery.addEventListener('change', this.handleViewportChange)
    document.addEventListener('click', this.handleDocumentClick)
    this.initPageLayout()
    this.getUserTeam()
  },
  beforeUnmount() {
    this.layoutActive = false
    this.viewportQuery?.removeEventListener('change', this.handleViewportChange)
    document.removeEventListener('click', this.handleDocumentClick)
    this.clearUserCardCloseTimer()
  },
  methods: {
    trapTeamNavigationFocus(event) {
      if (!this.teamNavigationOverlay) return
      const controls = [...(this.$refs.teamNavigation?.querySelectorAll('button:not([disabled]), [href], [tabindex="0"]') || [])]
        .filter(node => node.getClientRects().length)
      if (!controls.length) return
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && (document.activeElement === last || !this.$refs.teamNavigation.contains(document.activeElement))) { event.preventDefault(); first.focus() }
    },
    teamNavigationIcon(code) {
      return { '1_1': 'OfficeBuilding', '1_2': 'User', '1_3': 'Checked', '1_4': 'Key', '1_5': 'Wallet', '1_6': 'DataLine' }[code] || 'OfficeBuilding'
    },
    toggleTeamNavigation() {
      if (this.displayState.layoutTier === 'wide') this.teamWideCollapsed = !this.teamWideCollapsed
      else this.teamNavigationOpen = !this.teamNavigationOpen
      if (this.teamNavigationOpen) this.$nextTick(() => this.$refs.teamNavigation?.querySelector('[aria-current="page"]')?.focus({ preventScroll: true }))
    },
    closeTeamNavigation() {
      if (!this.teamNavigationOpen) return
      this.teamNavigationOpen = false
      this.$nextTick(() => this.$refs.teamMenuToggle?.focus({ preventScroll: true }))
    },
    openUserCardOnHover() {
      if (!this.viewportQuery?.matches) this.openUserCard()
    },
    openUserCard() {
      this.clearUserCardCloseTimer()
      this.userCardVisible = true
    },
    toggleUserCard() {
      if (this.userCardVisible) {
        this.closeUserCard()
      } else {
        this.openUserCard()
      }
    },
    scheduleUserCardClose() {
      this.clearUserCardCloseTimer()
      this.userCardCloseTimer = window.setTimeout(() => {
        const card = this.$refs.userCard
        if (!card || !card.contains(document.activeElement)) {
          this.closeUserCard()
        }
      }, 140)
    },
    handleUserCardFocusOut() {
      this.scheduleUserCardClose()
    },
    handleDocumentClick(event) {
      const card = this.$refs.userCard
      if (card && !card.contains(event.target)) {
        this.closeUserCard()
      }
    },
    clearUserCardCloseTimer() {
      if (this.userCardCloseTimer) {
        window.clearTimeout(this.userCardCloseTimer)
        this.userCardCloseTimer = null
      }
    },
    closeUserCard() {
      this.clearUserCardCloseTimer()
      this.userCardVisible = false
    },
    handleViewportChange(event) {
      const focusedElement = document.activeElement
      const shouldRestoreFocus = this.$refs.teamNavigation?.contains(focusedElement) ||
        focusedElement?.closest('.org-admin-mobile-menu-popper')
      this.isMobileView = event.matches
      this.closeUserCard()
      this.$nextTick(() => {
        if (!this.layoutActive || !shouldRestoreFocus) return
        if (this.isMobileView) {
          this.$refs.mobileNavigation?.focus()
        } else {
          const navigation = this.$refs.teamNavigation
          const activeMenu = navigation?.querySelector('[aria-current="page"]')
          const focusTarget = activeMenu || navigation
          focusTarget?.focus({ preventScroll: true })
        }
      })
    },
    async userLogout() {
      await this.$store.dispatch('user/logout')
      this.$router.push('/login')
    },
    openMenu(key, keyPath) {
      // console.log(key, keyPath)
    },
    closeMenu(key, keyPath) {
      // console.log(key, keyPath)
    },
    changeNav(item) {
      this.closeTeamNavigation()
      this.curPath = item.path
      this.menuActiveIndex = item.code
      this.$router.push({name: item.name})
    },
    changeMobileNav(code) {
      const item = this.mobileMenuItems.find(menu => menu.code === code)
      if (item) this.changeNav(item)
    },
    canShowMenu(item) {
      if (item.ownerOnly && Number(this.currTeamInfo.belong_user_id) !== Number(this.$store.getters['user/user_id'])) {
        return false
      }
      const requiredRoles = item.teamRoles || (item.teamRole ? [item.teamRole] : [])
      return requiredRoles.length === 0 || requiredRoles.every(role => this.currentTeamRoles.includes(role))
    },
    async getUserTeam() {
      try {
        await this.$store.dispatch('team/load')
      } catch (error) {
        if (this.layoutActive) this.$router.push('/')
        return
      }
      if (!this.layoutActive) return
      if (!this.currTeamInfo.team_id) {
        this.accessChecked = false
        this.$message.error(this.$t('layout.layout.no_management_permission'))
        this.$router.push('/')
        return
      }
      this.checkCurrentAccess()
    },
    checkCurrentAccess() {
      if (!this.layoutActive) return
      // 切换上下文时先卸载旧业务页，待store的新团队信息就绪再挂载。
      if (Number(this.currTeamInfo.team_id) !== this.currentTeamId) {
        this.accessChecked = false
        return
      }
      if (!this.currTeamInfo.team_id || Number(this.currTeamInfo.team_type) !== 0 || this.currentTeamRoles.length === 0) {
        this.accessChecked = false
        this.$message.error(this.$t('layout.layout.no_management_permission'))
        this.$router.push('/')
        return
      }
      const currentMenu = this.findMenuByName(this.visibleMenus, this.$route.name)
      if (!currentMenu) {
        this.accessChecked = false
        const firstMenu = this.visibleMenus[0] && this.visibleMenus[0].children[0]
        if (firstMenu) this.changeNav(firstMenu)
      } else {
        this.accessChecked = true
        this.initPageLayout(this.$route.name)
      }
    },
    initMenuActiveIndex(pathName) {
      this.menuActiveIndex = this.getCode(this.visibleMenus, pathName)
    },
    initPageLayout(pathName) {
      if (!pathName) {
        pathName = this.$route.name
      }
      this.initMenuActiveIndex(pathName)
    },
    getCode(menu, pathName) {
      let code = ''
      for (let i = 0; i < menu.length; i++) {
        if (typeof menu[i].children === 'undefined' || menu[i].children.length < 1) {
          if (menu[i].name.indexOf(pathName) > -1) {
            code = menu[i].code
            break
          }
        } else {
          code = this.getCode(menu[i].children, pathName)
          if (code !== '') {
            break
          }
        }
      }
      return code
    },
    findMenuByName(menu, pathName) {
      for (const item of menu) {
        if (item.name === pathName) return item
        const child = item.children && this.findMenuByName(item.children, pathName)
        if (child) return child
      }
      return null
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/iconfont/iconfont.css';

.org-admin-layout-container {
  --admin-surface: var(--agst-bg-color, #fff);
  --admin-page: var(--agst-bg-color-page, #f6f8fc);
  --admin-text: var(--agst-text-color-primary, #1f2937);
  --admin-muted: var(--agst-text-color-secondary, #7b8798);
  --admin-line: var(--agst-border-color-lighter, #edf0f5);
  --admin-accent: var(--agst-color-primary, #2d66f0);
  position: absolute;
  inset: 0;
  overflow: hidden;
  color: var(--admin-text);
  background: var(--admin-page);
  font-family: "Avenir Next", "PingFang SC", "Microsoft YaHei", sans-serif;

  .org-admin-container {
    position: relative;
    height: 100%;
  }

  .header-bar {
    z-index: 10;
    height: 72px !important;
    padding: 0;
    border-bottom: 1px solid var(--admin-line);
    background: var(--admin-surface);
    box-shadow: 0 8px 28px rgba(36, 54, 91, .05);
    backdrop-filter: blur(16px);
  }

  .header-inner {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
  }

  .title-wrap,
  .header-team-identity,
  .back-home a,
  .options {
    display: flex;
    align-items: center;
  }

  .header-team-identity {
    min-width: 0;
    gap: 10px;
    color: var(--admin-text);

    strong {
      max-width: min(360px, 42vw);
      overflow: hidden;
      font-size: 15px;
      font-weight: 700;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .header-team-avatar {
    display: grid;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    place-items: center;
    overflow: hidden;
    border: 1px solid var(--admin-line);
    border-radius: 8px;
    color: var(--admin-accent);
    background: var(--admin-page);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .line {
    width: 1px;
    height: 28px;
    margin: 0 18px;
    background: var(--admin-line);
  }

  .back-home a {
    gap: 7px;
    color: var(--admin-muted) !important;
    font-size: 13px;
    text-decoration: none;
    transition: color 160ms ease;

    &:hover { color: var(--admin-accent) !important; }
  }

  .options { gap: 12px; }

  .user-profile {
    position: relative;
    z-index: 20;
  }

  .user-profile-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
    border: 1px solid transparent;
    border-radius: 12px;
    background: transparent;
    cursor: pointer;
    transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease;

    &:hover,
    &:focus-visible,
    &[aria-expanded="true"] {
      border-color: var(--admin-line);
      outline: none;
      background: var(--admin-page);
      box-shadow: 0 4px 16px rgba(36, 54, 91, .08);
    }
  }

  .user-profile-card {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 260px;
    padding: 16px;
    border: 1px solid var(--admin-line);
    border-radius: 14px;
    background: var(--admin-surface);
    box-shadow: 0 18px 42px rgba(36, 54, 91, .16);
    color: var(--admin-text);

    &::before {
      position: absolute;
      top: -6px;
      right: 18px;
      width: 11px;
      height: 11px;
      border-top: 1px solid var(--admin-line);
      border-left: 1px solid var(--admin-line);
      background: var(--admin-surface);
      content: '';
      transform: rotate(45deg);
    }
  }

  .user-profile-card-head {
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 0;
  }

  .user-profile-copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 4px;

    strong,
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong { font-size: 14px; font-weight: 700; }
    span { color: var(--admin-muted); font-size: 11px; }
  }

  .user-points-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 15px;
    padding-top: 13px;
    border-top: 1px solid var(--admin-line);
    color: var(--admin-muted);
    font-size: 12px;

    strong {
      margin-left: auto;
      color: var(--admin-text);
      font-size: 15px;
    }
  }

  .user-points-icon {
    display: grid;
    width: 24px;
    height: 24px;
    place-items: center;
    border-radius: 7px;
    color: var(--admin-accent);
    background: rgba(45, 102, 240, .1);
    font-size: 13px;
  }

  .user-card-fade-enter-active,
  .user-card-fade-leave-active {
    transition: opacity 140ms ease, transform 140ms ease;
  }

  .user-card-fade-enter-from,
  .user-card-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px) scale(.98);
  }

  .org-admin-body-container {
    position: relative;
    height: calc(100% - 72px);
    background: var(--admin-page);
  }

  .org-admin-layout-aside {
    width: 252px !important;
    flex: 0 0 252px;
    overflow-y: auto;
    border-right: 1px solid var(--admin-line);
    background: var(--admin-surface);
  }

  .aside-inner {
    padding: 24px 14px;
  }

  .company-menu-box {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .company-menu-top-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .company-menu-sub-item {
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    font: inherit;
  }

  .top-item-text {
    display: flex;
    min-height: 44px;
    align-items: center;
    gap: 9px;
    margin: 0;
    padding: 0 12px;
    border-radius: 8px;
    color: var(--admin-text);
    font-size: 13px;
    font-weight: 500;
  }

  .company-menu-sub-item .top-item-text {
    min-height: 44px;
    color: var(--admin-text);
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: background 160ms ease, color 160ms ease, transform 160ms ease;

    &:hover {
      background: var(--admin-page);
      transform: translateX(2px);
    }

    &.active {
      color: var(--admin-accent);
      background: rgba(45, 102, 240, .1);
      //box-shadow: inset 3px 0 0 var(--admin-accent);
      font-weight: 700;
    }
  }


  .org-admin-layout-main {
    min-width: 0;
    overflow: auto;
    padding: 0;
    background: var(--admin-page);
  }

  .org-admin-main-inner {
    min-height: 100%;
  }

  @media (max-width: 800px) {
    .header-inner { padding: 0 14px; }
    .line { margin: 0 12px; }
    .org-admin-layout-aside {
      width: 210px !important;
      flex-basis: 210px;
    }
    .aside-inner { padding: 18px 10px; }
  }

}

@import '@/views/mobile/org_admin/styles/shell';
@import '@/views/mobile/org_admin/styles/ipad';

:global(html.dark) .org-admin-layout-container {
  .header-bar,
  .org-admin-layout-aside { background: var(--agst-bg-color, #17191f); }
  .org-admin-main-inner { color: var(--agst-text-color-primary, #edf1f8); }
}
</style>

<style lang="scss">
@import '@/views/mobile/user_center/styles/team_entry_dialogs';
</style>
